import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";

import { animated, useSpring } from "react-spring"; // For animated number

import { useTheme } from "@mui/material/styles";
import { Box, Chip, CircularProgress, Divider, Grid } from "@mui/material";

import PaperFragment from "../../../helper-components/misc/PaperFragment";

/**
 * Statistics component, displays various statistics about the database.
 */
export default function StatisticsGraphs(props) {
    const isMobile = props.browser.isMobile;

    const examinationStatistics = props.backend.examinationStatistics;
    const patientStatistics = props.backend.patientStatistics;

    const examinationTotal = examinationStatistics?.total || 0;
    const patientTotal = patientStatistics?.total || 0;

    // Animation for total number
    const { number: examinationTotalEl } = useSpring({ from: { number: 0 }, number: examinationTotal, config: { duration: 1000 } });
    const { number: patientTotalEl } = useSpring({ from: { number: 0 }, number: patientTotal, config: { duration: 1400 } });

    const isLoading = !examinationStatistics || !patientStatistics;

    const theme = useTheme();
    const chartTheme = theme.palette.mode === "dark" ? "dark" : undefined;

    // Set the theme for ECharts based on the current theme mode
    echarts.registerTheme("dark", {
        backgroundColor: "#1e1e1e",
        textStyle: {
            color: "#ffffff"
        },
        legend: {
            textStyle: {
                color: "#ffffff"
            }
        }
    });

    const getColorByStatus = (status) => {
        switch (status) {
            case "AWAITING_APPROVAL":
                return "rgb(250,200,88)";
            case "IN_QUEUE":
                return "rgb(173, 223, 252)";
            case "PROCESSED":
                return "rgb(127, 187, 99)";
            case "ERROR":
                return "rgb(219,44,44)";
            case "IN_PROGRESS":
                return "rgb(84,112,198)";

            case "APPROVED":
                return "rgb(84,112,198)";
            case "REJECTED":
                return "rgb(219,44,44)";
            case "COMPLETED":
                return "rgb(127, 187, 99)";

            default:
                return undefined;
        }
    };

    const getColorByType = (status) => {
        switch (status) {
            case "UNCATEGORIZED":
                return "rgb(153, 152, 150)";
            case "LECTURE":
                return "rgb(119, 184, 221)";
            case "PODCAST":
                return "rgb(230, 133, 7)";
            case "SHORT":
                return "rgb(206, 133, 221)";
            default:
                return undefined;
        }
    };

    const formatName = (name) => {
        // Split by underscore and capitalize first letter of each word
        const words = name.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        return words.join(" ");
    };

    const statusChartOption = (statistics) => {
        return {
            tooltip: {
                trigger: "item",
                formatter: "{a} <br/>{b}: {c} ({d}%)"
            },
            legend: {
                orient: "vertical",
                left: "left",
                data: statistics.by_status.map((item) => formatName(item[0]))
            },
            series: [
                {
                    name: "Status",
                    type: "pie",
                    radius: ["0%", "50%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        position: "outside",
                        formatter: "{b}: {c} ({d}%)"
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: "12",
                            fontWeight: "bold"
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: statistics.by_status.map((item) => ({
                        value: item[1],
                        name: formatName(item[0]),
                        itemStyle: {
                            color: getColorByStatus(item[0])
                        }
                    }))
                }
            ]
        };
    };

    const createdOnChartOption = (statistics) => {
        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "10%"
            },
            xAxis: {
                type: "category",
                data: statistics.by_created_on.map((item) => item[0]),
                boundaryGap: false
            },
            yAxis: {
                type: "value"
            },
            series: [
                {
                    name: "Created",
                    type: "line",
                    data: statistics.by_created_on.map((item) => item[1])
                }
            ]
        };
    };

    const typeChartOption = (statistics) => {
        return {
            tooltip: {
                trigger: "item",
                formatter: "{a} <br/>{b}: {c} ({d}%)"
            },
            legend: {
                orient: "vertical",
                left: "left",
                data: statistics.by_type.map((item) => formatName(item[0]))
            },
            series: [
                {
                    name: "Type",
                    type: "pie",
                    radius: ["0%", "50%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        position: "outside",
                        formatter: "{b}: {c} ({d}%)"
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: "12",
                            fontWeight: "bold"
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: statistics.by_type.map((item) => ({
                        value: item[1],
                        name: formatName(item[0]),
                        itemStyle: {
                            color: getColorByType(item[0])
                        }
                    }))
                }
            ]
        };
    };
    const patientsChartOption = (statistics) => {
        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "15%",
                left: "7%",
                right: "5%",
                height: "70%"
            },
            xAxis: {
                type: "category",
                data: statistics.by_patients.map((item) => {
                    const [first, ...rest] = item[0].split(" ");
                    const last = rest.length ? rest[rest.length - 1] : "";
                    return `${first.charAt(0)}. ${last}`;
                }),
                axisLabel: {
                    rotate: 65, // Rotate labels
                    textStyle: {
                        fontSize: 11 // Smaller font size
                    }
                }
            },
            yAxis: {
                type: "value"
            },
            series: [
                {
                    name: "Examinations",
                    type: "bar",
                    data: statistics.by_patients.map((item) => item[1])
                }
            ]
        };
    };
    const examinersChartOption = (statistics) => {
        return {
            tooltip: {},
            grid: {
                top: "5%",
                bottom: "15%",
                left: "7%",
                right: "5%",
                height: "70%"
            },
            xAxis: {
                type: "category",
                data: statistics.by_examiners.map((item) => {
                    const [username, ...email] = item[0].split(" (");
                    return `${username}`;
                }),
                axisLabel: {
                    rotate: 65, // Rotate labels
                    textStyle: {
                        fontSize: 11 // Smaller font size
                    }
                }
            },
            yAxis: {
                type: "value"
            },
            series: [
                {
                    name: "Examinations",
                    type: "bar",
                    data: statistics.by_examiners.map((item) => item[1])
                }
            ]
        };
    };

    return (
        <Grid container pl={1}>
            <Grid item xs={12} sm={6}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Total examinations"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{examinationTotalEl.to((n) => Math.floor(n))}</animated.div>}
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <PaperFragment
                    center
                    height={isMobile ? "75px" : "125px"}
                    title="Total patients"
                    value={<animated.div style={{ fontSize: "1.5em", fontWeight: "bold" }}>{patientTotalEl.to((n) => Math.floor(n))}</animated.div>}
                />
            </Grid>

            {isLoading && (
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 2, height: "200px" }}>
                        <CircularProgress />
                    </Box>
                </Grid>
            )}

            {!isLoading && (
                <>
                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Examination Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment
                            title="Examinations By Patient"
                            value={
                                <ReactECharts
                                    theme={chartTheme}
                                    option={patientsChartOption(examinationStatistics)}
                                    style={{
                                        height: "300px"
                                    }}
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PaperFragment
                            title="Examinations By Examiner"
                            value={
                                <ReactECharts
                                    theme={chartTheme}
                                    option={examinersChartOption(examinationStatistics)}
                                    style={{
                                        height: "300px"
                                    }}
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <PaperFragment
                            title="Examinations Created Over Time"
                            value={
                                <ReactECharts
                                    theme={chartTheme}
                                    option={createdOnChartOption(examinationStatistics)}
                                    style={{
                                        height: "250px"
                                    }}
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ m: 1 }}>
                            <Chip label="Patient Statistics" variant="outlined" size="small" />
                        </Divider>
                    </Grid>

                    <Grid item xs={12}>
                        <PaperFragment
                            title="Patients Created Over Time"
                            value={
                                <ReactECharts
                                    theme={chartTheme}
                                    option={createdOnChartOption(patientStatistics)}
                                    style={{
                                        height: "250px"
                                    }}
                                />
                            }
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}
